import jsPDF from "jspdf";
import { TRIBES } from "@/lib/tribes";

// Draw a certificate onto a canvas (for PNG share)
export function drawCertificateOnCanvas(canvas, child, cert) {
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;
  const w = 1200, h = 900;
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = tribe.bg; ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = tribe.accent; ctx.lineWidth = 8; ctx.strokeRect(50, 50, w - 100, h - 100);
  ctx.lineWidth = 2; ctx.strokeRect(80, 80, w - 160, h - 160);
  const inkColor = tribe.key === "nasal_ninja" ? "#FFFEF9" : "#1E1E2E";
  ctx.textAlign = "center";
  ctx.fillStyle = tribe.accent;
  ctx.font = "bold 28px Figtree, Nunito, sans-serif";
  ctx.fillText((child.is_self ? "Certificate of Progress" : (child.tribe === "neutral" ? "Breathe · Thrive" : tribe.name)).toUpperCase(), w / 2, 190);
  ctx.fillStyle = inkColor;
  ctx.font = "bold 60px Figtree, Nunito, sans-serif";
  ctx.fillText("Certificate of Session", w / 2, 300);
  ctx.font = "24px Nunito, sans-serif";
  ctx.fillText("This certifies that", w / 2, 380);
  ctx.fillStyle = tribe.accent;
  ctx.font = "bold 96px Figtree, Nunito, sans-serif";
  ctx.fillText(child.name, w / 2, 500);
  ctx.fillStyle = inkColor;
  ctx.font = "30px Nunito, sans-serif";
  ctx.fillText(`has completed Session ${cert.session_number} of Breathe to Thrive`, w / 2, 580);
  ctx.font = "22px Nunito, sans-serif";
  ctx.fillText(new Date(cert.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }), w / 2, 640);
  ctx.font = "italic 20px Nunito, sans-serif";
  ctx.fillStyle = inkColor;
  ctx.globalAlpha = 0.7;
  ctx.fillText("Progress, not perfection.", w / 2, 810);
  ctx.globalAlpha = 1;
}

export function certificatePdf(child, cert) {
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFillColor(tribe.bg);
  doc.rect(0, 0, w, h, "F");
  doc.setDrawColor(tribe.accent);
  doc.setLineWidth(4);
  doc.rect(30, 30, w - 60, h - 60);
  doc.setLineWidth(1);
  doc.rect(45, 45, w - 90, h - 90);
  const inkColor = tribe.key === "nasal_ninja" ? "#FFFEF9" : "#1E1E2E";
  doc.setTextColor(inkColor);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text((child.is_self ? "Certificate of Progress" : (child.tribe === "neutral" ? "Breathe · Thrive" : tribe.name)).toUpperCase(), w / 2, 100, { align: "center" });
  doc.setFontSize(36);
  doc.text("Certificate of Session", w / 2, 170, { align: "center" });
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text("This certifies that", w / 2, 220, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.setTextColor(tribe.accent);
  doc.setFontSize(48);
  doc.text(child.name, w / 2, 290, { align: "center" });
  doc.setTextColor(inkColor);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(16);
  doc.text(`has completed Session ${cert.session_number} of Breathe to Thrive`, w / 2, 340, { align: "center" });
  doc.setFontSize(12);
  doc.text(new Date(cert.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }), w / 2, 400, { align: "center" });
  doc.setFontSize(10);
  doc.text("Progress, not perfection.", w / 2, h - 80, { align: "center" });
  return doc;
}

// Provider handoff — summary PDF of check-ins, roadblocks, notes
export function providerPdf(child, checkins, roadblocks, streak) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const w = doc.internal.pageSize.getWidth();
  let y = 60;
  doc.setTextColor("#1E1E2E");
  doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.text("Breathe to Thrive · Provider Summary", 50, y); y += 30;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  doc.setTextColor("#4A4A6A");
  doc.text(`Prepared ${new Date().toLocaleDateString()}  ·  ${child.is_self ? "Self-enrolled" : "Child"}: ${child.name}${child.age ? ` (age ${child.age})` : ""}  ·  Session ${child.current_session} of 12`, 50, y); y += 18;
  const tribeLabel = child.is_self ? "Self-enrolled (no tribe)" : ((TRIBES[child.tribe] || {}).name || child.tribe || "—");
  doc.text(`Tribe: ${tribeLabel}  ·  Appliance in use: ${child.appliance_in_use ? "yes" : "no"}`, 50, y); y += 18;
  if (streak) { doc.text(`Practice streak: current ${streak.current} · longest ${streak.longest} · total practices ${streak.total}`, 50, y); y += 24; }

  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor("#2C2D5B");
  doc.text("Reported symptoms at enrollment", 50, y); y += 16;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor("#1E1E2E");
  const symp = (child.top_symptoms || []).join(", ") || "none";
  doc.text(doc.splitTextToSize(symp, w - 100), 50, y); y += 30;

  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor("#2C2D5B");
  doc.text("Check-in trend", 50, y); y += 16;
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor("#4A4A6A");
  const cols = [["Session", 50], ["Date", 100], ["Nasal %", 180], ["Lip Seal %", 240], ["Tongue %", 310], ["Swallow", 370], ["Cong.", 430]];
  cols.forEach(([label, x]) => doc.text(label, x, y));
  y += 12;
  doc.setDrawColor("#E0D8E8"); doc.line(50, y - 6, w - 50, y - 6);
  doc.setFont("helvetica", "normal"); doc.setTextColor("#1E1E2E");
  (checkins || []).forEach((c) => {
    const b = c.breathing || {}, l = c.lips || {}, t = c.tongue || {}, s = c.swallow || {}, cg = c.congestion || {};
    const nasal = Math.round(((b.nasal_day_pct || 0) + (b.nasal_night_pct || 0)) / 2);
    const tongue = Math.round(((t.tip_spot_pct || 0) + (t.mid_palate_pct || 0) + (t.back_palate_pct || 0)) / 3);
    const row = [String(c.session_number), new Date(c.created_at).toLocaleDateString(), `${nasal}%`, `${l.sealed_pct || 0}%`, `${tongue}%`, s.proper_easy || "-", cg.blocking_nose || "-"];
    cols.forEach(([_, x], i) => doc.text(row[i], x, y));
    y += 14;
    if (y > 700) { doc.addPage(); y = 60; }
  });
  y += 12;

  if ((roadblocks || []).length) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor("#2C2D5B");
    doc.text("Structural roadblocks", 50, y); y += 16;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor("#1E1E2E");
    roadblocks.forEach((r) => {
      const grade = r.severity != null ? ` (grade ${r.severity})` : "";
      doc.text(`• ${r.kind.replace(/_/g, " ")}: ${r.status}${grade} — ${r.note || "no notes"}`, 55, y);
      y += 14;
      if (y > 720) { doc.addPage(); y = 60; }
    });
    y += 8;
  }

  // Wins / struggles from most recent check-in
  const last = (checkins || [])[checkins.length - 1];
  if (last && (last.struggles || last.wins || last.top_symptoms)) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor("#2C2D5B");
    doc.text("Parent notes (most recent check-in)", 50, y); y += 16;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor("#1E1E2E");
    if (last.top_symptoms) { doc.text("Top symptoms: " + last.top_symptoms, 55, y); y += 14; }
    if (last.struggles) { doc.text(doc.splitTextToSize("Struggles: " + last.struggles, w - 110), 55, y); y += 26; }
    if (last.wins) { doc.text(doc.splitTextToSize("Wins: " + last.wins, w - 110), 55, y); y += 26; }
  }
  doc.setFontSize(8); doc.setTextColor("#7A7A9A");
  doc.text("Generated by Breathe to Thrive · progress, not perfection", 50, doc.internal.pageSize.getHeight() - 30);
  return doc;
}
